import React, {createRef, useState, useContext} from 'react';
import __ from "lodash";
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import mailtoLink from "mailto-link";
import MaterialTable from "@material-table/core";
import { Checkbox, MenuItem, Select, TablePagination, TextField, useTheme } from "@material-ui/core";
import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import FuelSupplierAirportsValidator from "../validators/FuelSupplierAirportsValidator";
import AirportLookup from "../../Airports/components/AirportsLookup";
import ExchangeRateProviderSelector from '../../Currencies/components/ExchangeRateProviderSelector';
import CurrencyLookup from "../../Currencies/components/CurrencyLookup";
import EditFormDialog from "../../../controls/FormDialog";
import ContactListLookup from "./ContactListLookup";
import { useFuelSupplierAirports,
  useAddFuelSupplierAirport,
  useUpdateFuelSupplierAirport,
  useDeleteFuelSupplierAirport } from "../../../services/companies";

import { defaultComponents } from '../../../controls/Material-Table-Components';
import { ComboSelect } from '../../../controls';

const isValidHttpUrl = (string) => {
  try {
    const url = new URL(string);
  } catch (_) {
    return false;
  }

  return true;
}

const getButtons = (process) => {

  const buttons = {
    cancel:{icon: "cancel", label: "Close", color: "primary"}
  };

  switch (process) {
    case "EMAIL":
      buttons['save'] = {icon: "send", label: "Email Supplier", color: "primary", disabled: (status)=>{return status.error}};
      break;
    case "PHONE":
      buttons['save'] = {icon: "phone", label: "Call Supplier", color: "primary", disabled: (status)=>{return status.error}};
      break;
    case "WEB":
      buttons['save'] = {icon: "open_in_browser", label: "Open Web Form", color: "primary", disabled: (status)=>{return status.error}};
      break;
    default:
      break;
  }

  return buttons;
}




const FuelSupplierAirportList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const validatorColumn = ValidatorColumn(FuelSupplierAirportsValidator, true);

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null
  });
  const [dialogState, setDialogState] = useState({
    open: false,
  })

  const parentRecord = props.record;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleAddFuelSupplierAirport = useAddFuelSupplierAirport();
  const handleUpdateFuelSupplierAirport = useUpdateFuelSupplierAirport();
  const handleDeleteFuelSupplierAirport = useDeleteFuelSupplierAirport();

  const queryClient = useQueryClient();
  const extraKeys = [parentRecord.companyUuid];

  const fuelSupplierAirports = useFuelSupplierAirports({tableRef, queryClient, extraKeys});

  const getColumns = (props) => {
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };

    const defaultEditProps = {
      style:{height: "auto"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(90)),
        title: "Airport",
        field: "airportCode",
        editable: state.canEdit ? "onAdd" : "never",
        cellStyle: state.canEdit?{ width: 66, minWidth: 66, maxWidth: 66, padding: "8px 2px" }:{ width: 66, minWidth: 66, maxWidth: 66, padding: "8px 12px" },
        headerStyle: state.canEdit?{padding: "8px 2px" }:{padding: "8px 12px" },
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(180)),
        title: "Exchange Rate Provider",
        field: "exchangeRateType",
        editComponent: (props) => (
          <ExchangeRateProviderSelector
            type="FUEL"
            value={props.value}
            onChange={props.onChange}
            includeOther={true}
          />
        )
      },
      {
        ...(ColumnWidth(100)),
        title: "Currency",
        field: "defaultCurrencyCode",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "USD",
        editComponent: (props) => (
          <CurrencyLookup
            showChildCurrencies={true}
            value={props.value}
            onChange={props.onChange}
            style={{ fontSize: "0.875rem", textAlign: "right"}}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Contract Type",
        field: "contractType",
        editable: props.canEdit ? "always" : "never",
        initialEditValue: SystemData.contractTypes[0],
        editComponent: (props) => (
          <Select
            labelId="Contract Type"
            id="Contract Type"
            value={props.value}
            onChange={e => {
              props.onChange(e.target.value);
            }}
          >
            {SystemData.contractTypes.map((item) => {
              return (<MenuItem key={item} value={item}>{item}</MenuItem>);
            })
            }
          </Select>
        ),
        render: (rowData) => (<div>{rowData.contractType}</div>),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Price Period",
        field: "pricePeriod",
        editable: props.canEdit ? "always" : "never",
        initialEditValue: SystemData.pricePeriods[0],
        editComponent: (props) => (
          <Select
            labelId="Price Period"
            id="Price Period"
            value={props.value}
            onChange={e => {
              props.onChange(e.target.value);
            }}
          >
            {SystemData.pricePeriods.map((item) => {
              return (<MenuItem key={item} value={item}>{item}</MenuItem>);
            })
            }
          </Select>
        ),
        render: (rowData) => (<div>{rowData.pricePeriod}</div>),
      },
      {
        ...baseColumn,
        title: "Provider",
        field: "fuelProvider",
        editable: props.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "",
        editComponent: (props) => (
          <TextField
            placeholder=''
            value={props.value}
            onChange={(e)=>{
              const fuelProvider = e.target.value;
              let fuelProviderType = props.rowData.fuelProviderType;
              if (fuelProvider === "") {
                fuelProviderType = "";
              } else {
                if (fuelProviderType === "") {
                  fuelProviderType = "ITP";
                }
              }
              props.onRowDataChange({...props.rowData, fuelProvider, fuelProviderType});
            }}
            inputProps={defaultEditProps}
          />
        ),
        render: (rowData) => (<div>{rowData.fuelProvider}</div>),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(120)),
        title: "Provider Type",
        field: "fuelProviderType",
        editable: (rowData => props.canEdit && !__.isEmpty(rowData.fuelProvider)? "always" : "never"),
        emptyValue: "",
        initialEditValue: 'ITP',
        editComponent: (props) => (
          <ComboSelect
            group="FUEL PROVIDER TYPE"
            subGroup=""
            value={props.value || ''}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <ComboSelect
            group="FUEL PROVIDER TYPE"
            subGroup=""
            value={rowData.fuelProviderType}
            displayOnly={true}
          />
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, {textAlign: 'center'}, {textAlign: 'center'})),
        title: "Default Supplier",
        field: "defaultSupplier",
        editable: props.canEdit ? "always" : "never",
        type: "boolean",
        emptyValue: false,
        initialEditValue: true, 
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.defaultSupplier === true} value="true" />        
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{ marginLeft: 50 }}>
      <MaterialTable
        tableRef={tableRef}
        style={{
          backgroundColor: "#fdfdfd",
          border: "2px solid #aaaaaa",
          boxShadow: "none",
          borderRadius: 0,
        }}
        options={{
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: theme.palette.childTableHeader.backgroundColor,
            color: theme.palette.childTableHeader.textColor,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: 5,
          detailPanelType: "single",
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        actions={[
          (rowData) => (
            {
              hidden: false,
              icon: `${parentRecord.fuelOrderProcess === "EMAIL" ? 'mail_outline' : parentRecord.fuelOrderProcess === "WEB" ? 'open_in_browser' : 'phone'}`,
              iconProps: { style: { color: theme.palette.primary.main }},
              tooltip: "Fuel Supplier Contacts",
              isFreeAction: false,
              onClick: (e, rowData) => {
                setDialogState({open: true, rowData: rowData});
              }
            }
          ),
        ]}
        title="Fuel Supplier Airport Management"
        columns={getColumns({canEdit: state.canEdit})}
        data={fuelSupplierAirports.data}
        editable={{
          isEditable: () => {
            return state.canEdit;
          },
          isDeletable: () => {
            return state.canEdit;
          },
          onRowAdd: state.canEdit
            ? (newData) => new Promise((resolve, reject) => {
              handleAddFuelSupplierAirport.mutate({record: {companyUuid: parentRecord.companyUuid, ...newData}, resolve, reject, extraKeys});
            })
            : undefined,
          onRowUpdate: (newData, oldData) =>
            new Promise((resolve, reject) => {
              handleUpdateFuelSupplierAirport.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
            }),
          onRowDelete: (oldData) =>
            new Promise((resolve, reject) => {
              handleDeleteFuelSupplierAirport.mutate({oldRecord: oldData, resolve, reject, extraKeys});
            }),
        }}
      />
      {dialogState.open?
        <EditFormDialog
          title={`Fuel Supplier Contacts at ${dialogState.rowData.airportCode} (Order Process: ${parentRecord.fuelOrderProcess})`}
          message={`Select contacts and click ${parentRecord.fuelOrderProcess === "EMAIL" ? '\'Email Supplier\'' : parentRecord.fuelOrderProcess === "WEB" ? '\'Open Web Form\'' : '\'Call Supplier\''}`}
          buttons={getButtons(parentRecord.fuelOrderProcess)}
          open={dialogState.open}
          record={dialogState.rowData}
          dialogOptions={{maxWidth: "sm", fullWidth: true}}
          onChange={(state) => {
            if (state.buttonPressed === "save") {
              if (parentRecord.fuelOrderProcess === "EMAIL") {
                if (dialogState.contactList === "") {
                  SystemData.openAlert(
                    "Error",
                    `At least one email address is required to open an email client.`
                  );
                }
                else {
                  const mailTo = mailtoLink({to: dialogState.contactList, subject: ``});
                  window.location.href = `${mailTo}`;
                }
              }
              if (parentRecord.fuelOrderProcess === "WEB" && dialogState.contactList) {
                const urls = dialogState.contactList.split(",");
                if (!urls.length > 0 || !isValidHttpUrl(urls[0]))
                  SystemData.openAlert(
                    "Error",
                    `Cannot open page - URL is invalid.`
                  );
                else
                  window.open(urls[0],'_newtab');
              }

              setDialogState({...dialogState, open: false, rowData: null, contactList: null});
            } else {
              setDialogState({...dialogState, open: false, rowData: null});
            }
          }}
        >
          <div>
            <ContactListLookup
              companyUuid={dialogState.rowData.companyUuid}
              airportCode={dialogState.rowData.airportCode}
              process={parentRecord.fuelOrderProcess}
              width={400}
              canEdit={props.canEdit}
              onChange={(list)=>{setDialogState({...dialogState, contactList: list})}}
            />
          </div>
        </EditFormDialog>
        : <></>
      }
    </div>
  );

}

export default FuelSupplierAirportList;
